import { useCallback, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Download, RotateCcw, Sparkles } from "lucide-react";
import { useAnalyticsFilters } from "./filters-context";
import {
  fetchExportDashboardPdf,
  type ExportPdfQueryParams,
} from "./api";
import { downloadPdf } from "@/shared/utils/pdf";
import { FiltersToolbar } from "./components/FiltersToolbar";
import { AnalyticsHeroCards } from "./components/AnalyticsHeroCards";
import { AnalyticsCharts } from "./components/AnalyticsCharts";
import { AnalyticsTablesSection } from "./components/AnalyticsTablesSection";

export function AnalyticsDashboard() {
  const { state: filters } = useAnalyticsFilters();
  const [isExporting, setIsExporting] = useState(false);

  const dateFromParam = filters.dateRange.from ?? undefined;
  const dateToParam = filters.dateRange.to ?? undefined;

  const windowDays = useMemo(() => {
    if (filters.dateRange.from && filters.dateRange.to) {
      const fromDate = new Date(`${filters.dateRange.from}T00:00:00`);
      const toDate = new Date(`${filters.dateRange.to}T00:00:00`);
      if (
        !Number.isNaN(fromDate.getTime()) &&
        !Number.isNaN(toDate.getTime())
      ) {
        const diffMs = toDate.getTime() - fromDate.getTime();
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24)) + 1;
        return Math.max(1, diffDays);
      }
    }
    return 30;
  }, [filters.dateRange.from, filters.dateRange.to]);

  const handleExportPdf = useCallback(async () => {
    setIsExporting(true);
    try {
      const params: ExportPdfQueryParams = {
        clienteIntermedioId: filters.clienteIntermedioId,
        clienteFinalId: filters.clienteFinalId,
        programaId: filters.programaId,
        actividadId: filters.actividadId,
        dateFrom: dateFromParam,
        dateTo: dateToParam,
      };
      const blob = await fetchExportDashboardPdf(params);
      const filename = `analytics-dashboard-${new Date().toISOString().split("T")[0]}.pdf`;
      downloadPdf(blob, filename);
    } catch (error) {
      console.error("Failed to export PDF:", error);
    } finally {
      setIsExporting(false);
    }
  }, [filters, dateFromParam, dateToParam]);

  return (
    <div className="mx-auto w-full space-y-4 sm:space-y-6 md:space-y-8 p-3 sm:p-4 md:p-6 lg:p-8">
      {/* Header */}
      <header className="relative overflow-hidden rounded-2xl sm:rounded-3xl border border-border/50 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 p-4 sm:p-5 md:p-6 text-slate-50 shadow-[0_35px_120px_-45px_rgba(15,23,42,0.65)]">
        <div className="absolute -right-6 top-0 h-40 w-40 rounded-full bg-sky-400/30 blur-3xl" />
        <div className="absolute -left-10 bottom-0 h-36 w-36 rotate-45 rounded-3xl bg-purple-500/25 blur-3xl" />
        <div className="relative flex flex-col gap-3 sm:gap-4">
          <div className="space-y-1 sm:space-y-2">
            <h1 className="flex items-center gap-2 text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight">
              <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-amber-300" />{" "}
              Analíticas
            </h1>
            <p className="text-xs sm:text-sm text-slate-300">
              Clientes, programas y desempeño de aprendices en tiempo real.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row flex-wrap items-start sm:items-center gap-2 sm:gap-3">
            <div className="rounded-full bg-white/10 px-3 sm:px-4 py-1 text-[0.7rem] sm:text-xs text-slate-100 backdrop-blur-sm">
              {dateFromParam && dateToParam
                ? `Periodo: ${new Date(dateFromParam).toLocaleDateString()} - ${new Date(dateToParam).toLocaleDateString()}`
                : `Últimos ${windowDays} días`}
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="bg-white/15 text-slate-100 backdrop-blur hover:bg-white/25 text-xs sm:text-sm h-8 sm:h-9"
              onClick={handleExportPdf}
              disabled={isExporting}
              data-testid="export-pdf-button"
            >
              <Download className="mr-1.5 sm:mr-2 h-3 w-3 sm:h-4 sm:w-4" />
              {isExporting ? "Exporting..." : "Exportar todo"}
            </Button>
          </div>
        </div>
      </header>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-2 px-3 sm:px-4 md:px-6 pt-3 sm:pt-4 md:pt-6">
          <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
            <RotateCcw className="h-3 w-3 sm:h-4 sm:w-4" />
            Filtros de contexto
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 sm:px-4 md:px-6 pb-3 sm:pb-4 md:pb-6">
          <FiltersToolbar />
        </CardContent>
      </Card>

      {/* Hero cards, Charts, Tables — each fetches its own data */}
      <AnalyticsHeroCards filters={filters} />
      <AnalyticsCharts filters={filters} />
      <AnalyticsTablesSection />
    </div>
  );
}
